<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { inputProps } from 'naive-ui';
import GaiaTextarea from './GaiaTextarea.vue';

/**
 * The chat composer's textarea: it grows with its content, Enter sends and
 * Shift+Enter starts a new line.
 *
 * Both composers had this by hand and both had the same bug — Enter while an
 * IME candidate window is open confirms the candidate, and without the
 * `isComposing` guard that also sent the half-typed message. Fixed once here.
 */
const props = defineProps({
  ...inputProps,
  autosize: {
    type: [Boolean, Object] as PropType<boolean | { minRows?: number; maxRows?: number }>,
    default: () => ({ minRows: 1 }),
  },
});

const emit = defineEmits<{
  'update:value': [value: string];
  /** Enter without Shift, and not mid-composition. */
  submit: [];
}>();

function handleKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Enter' || event.shiftKey) return;
  // A candidate window is open: Enter belongs to the IME, not to us.
  if (event.isComposing) return;
  event.preventDefault();
  emit('submit');
}

const textareaBindings = computed<Record<string, unknown>>(() => {
  const bindings: Record<string, unknown> = { ...props };
  delete bindings.onKeydown;
  return bindings;
});
</script>

<template>
  <gaia-textarea
    v-bind="textareaBindings"
    @update:value="(value: string) => emit('update:value', value)"
    @keydown="handleKeydown"
  />
</template>
